import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { EmployeeRolesSelector } from '~/scenes/PeopleModule/components/EmployeeActions/ChangeRole/EmployeeRolesSelector';
import {
  UserAccountLoginWithMethod,
  useCreateUserAccountDialog,
} from '~/scenes/PeopleModule/components/EmployeeActions/CreateUserAccountDialog/useCreateUserAccountDialog';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { NewUserAccountDialog } from './components/NewUserAcccountDialog/component';
import { useSxStyles } from './useStyles';

type PropsType = {
  employeeId: number;
  open: boolean;
  onClose: () => void;
};

export const CreateUserAccountDialog = (props: PropsType) => {
  const { open, onClose, employeeId } = props;

  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const {
    hasWorkEmail,
    isLoading,
    form: {
      control,
      formState: { isDirty, isValid },
      trigger,
    },
    languageOptions,
    updateUserNameByLoginMethod,
    isLoginWithSelectionEnabled,
    isAlternateEmailSmsEnabled,
    onCancel,
    onSubmit,
    onComplete,
    newAccountDetails,
    showNewPassword,
  } = useCreateUserAccountDialog({
    employeeId,
    onClose,
  });

  if ((!isAlternateEmailSmsEnabled && !hasWorkEmail) || isLoading) {
    return (
      <DraggableDialog onClose={onCancel} open={open}>
        <SolaForceDialogTitle onClose={onCancel}>
          {t('laNewEmpInfo')}
        </SolaForceDialogTitle>
        <DialogContent dividers={true}>
          {isLoading ? (
            <LoadingView />
          ) : (
            <Alert severity={'error'}>{t('laActiveEmpAccount')}</Alert>
          )}
        </DialogContent>
      </DraggableDialog>
    );
  }

  if (!!newAccountDetails) {
    return (
      <NewUserAccountDialog
        employeeId={employeeId}
        newAccountDetails={newAccountDetails}
        showNewPassword={showNewPassword}
        open={open}
        alternativeEmailEnabled={isAlternateEmailSmsEnabled}
        hasWorkEmail={hasWorkEmail}
        onClose={onComplete}
      />
    );
  }

  return (
    <DraggableDialog onClose={onCancel} open={open}>
      <SolaForceDialogTitle onClose={onCancel}>
        {t('laNewEmpInfo')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Box sx={sxStyles.formContent}>
          <Controller
            control={control}
            name={'roles'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <EmployeeRolesSelector
                  name={'roles'}
                  value={value}
                  onChange={(newValue) => {
                    onChange(newValue);
                    trigger();
                  }}
                  columnCount={2}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />

          <Controller
            control={control}
            name={'language'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <FormControl
                  variant={'outlined'}
                  error={!!error}
                  fullWidth={true}
                >
                  <InputLabel id={'user-language-label'}>
                    {t('laLanguage')}
                  </InputLabel>
                  <Select
                    labelId={'user-language-label'}
                    variant={'outlined'}
                    name={'fLoginUserLanguage'}
                    label={t('laLanguage')}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                  >
                    {languageOptions.map((language, index) => {
                      const { fComLanguageCode, fComLanguageName } = language;

                      return (
                        <MenuItem
                          value={fComLanguageCode}
                          key={`language-${index}`}
                        >
                          {fComLanguageName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              );
            }}
          />

          {isLoginWithSelectionEnabled && (
            <Controller
              control={control}
              name={'loginWith'}
              render={({ field: { value, onChange } }) => {
                return (
                  <FormControl>
                    <FormLabel id={'login-with-label'}>
                      {t('laLoginWith')}
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby={'login-with-label'}
                      name={'loginWith'}
                      value={value}
                      onChange={(event, value) => {
                        updateUserNameByLoginMethod(value);
                        onChange(event, value);
                      }}
                      row={true}
                    >
                      <FormControlLabel
                        value={UserAccountLoginWithMethod.Internal}
                        control={<Radio />}
                        label={'Solaforce'}
                      />
                      <FormControlLabel
                        value={UserAccountLoginWithMethod.External}
                        control={<Radio />}
                        label={t('laOther')}
                      />
                    </RadioGroup>
                    <FormHelperText>{t('laLoginHint')}</FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          <Controller
            control={control}
            name={'username'}
            render={({ field: { value, onChange } }) => {
              return (
                <TextField
                  name={'fLoginUsername'}
                  label={t('laLoginUsername')}
                  value={value}
                  onChange={onChange}
                  helperText={t('laUsernameHint')}
                />
              );
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button onClick={onSubmit} disabled={!isDirty || !isValid}>
          {t('create')}
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};
